/**
 * Canonical formatter for project display labels.
 *
 * Every UI surface that shows a project's identity should call this — never
 * inline `#${project.project_number}` or `${project.disease}` directly.
 * Lab users identify projects by integer project_number, so every variant
 * leads with the number; disease/PI come as supporting context.
 *
 * Project #0 is the "Unknown Project" sentinel (db/schema.sql:1121) for
 * specimens with missing/invalid project references. It renders as
 * "Unknown" rather than "#0".
 *
 * Accepts any object with project_number / disease / pi_name fields — works
 * with both project rows and denormalized specimen rows that carry those
 * columns.
 */
export function formatProjectLabel(project, { variant = 'short' } = {}) {
  if (!project) return 'Project';

  const num = project.project_number;

  if (num === 0) {
    return variant === 'title' ? 'Unknown Project' : 'Unknown';
  }

  if (num == null) {
    return project.disease || 'Project';
  }

  const disease = project.disease || 'Unnamed';

  switch (variant) {
    case 'title':
      return `Project ${num}`;
    case 'with-disease':
      return `${num} - ${disease}`;
    case 'with-collaborator':
      return project.pi_name
        ? `${num} - ${disease} (${project.pi_name})`
        : `${num} - ${disease}`;
    case 'short':
    default:
      return `#${num}`;
  }
}
